/**
 * Pure display formatters for the client-offer-review proposal screen. No
 * app-wide PLN formatter with this exact "exactly 2 decimals + zł suffix"
 * shape exists yet (see the sibling note in
 * `packages/screens/src/offers/versions/unit-decisions/format-helpers.ts`)
 * — kept feature-scoped here rather than introducing a third competing copy.
 */
import { roundMoney } from '@ooh/contracts';

/**
 * Null-safe PLN formatter — thousands-separated Polish number, always exactly
 * 2 decimal places, + " zł". Rounds through `roundMoney` before formatting
 * (half away from zero) rather than relying on `Intl`'s own rounding of the
 * raw float, so a displayed price can never disagree by a grosz with the same
 * value as summed by `computeOfferReviewAggregates`.
 */
export function fmtPln(v: number | null | undefined): string | null {
  if (v == null) return null;
  const rounded = roundMoney(v);
  const formatted = rounded.toLocaleString('pl-PL', {
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} zł`;
}

/** Reach formatter — values >=10 000 shown as rounded thousands + " tys.". */
export function fmtK(n: number): string {
  return n >= 10000 ? `${Math.round(n / 1000)} tys.` : n.toLocaleString('pl-PL');
}
